<template>
  <div class="profile-page">
    <!-- 顶部栏 -->
    <NavBar :title="t('profile.title')" :show-back="false" />
    <!-- 顶栏背景 -->
    <div
      class="profile-header-box"
      :style="{
        backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.1)), url(${profileBanner})`,
      }"
    >
      <!-- 设置按钮 -->
      <img
        class="profile-setting"
        src="@/assets/setting.png"
        alt="setting"
        @click="goToSetting"
      />
      <!-- 用户信息卡片 -->
      <div class="profile-card">
        <div class="profile-avatar" @click.capture="goToAvatar">
          <Avatar
            size="large"
            :avatar-index="userStore.avatarIndex"
            :userId="userStore.uid"
            :avatarFrame="userStore.avatarFrame"
          />
        </div>
        <!-- 用户称号展示 -->
        <div
          v-if="userStore.userTitle && userStore.userTitle.length > 0"
          class="profile-titles"
        >
          <span
            v-for="(title, index) in userStore.userTitle"
            :key="index"
            class="profile-title-badge"
          >
            {{ title }}
          </span>
        </div>
        <div class="profile-info">
          <div class="profile-name">
            {{ userStore.username }}
            <span class="profile-edit" @click="goToNicknameModify">{{
              t("profile.editNickname")
            }}</span>
          </div>

          <div class="profile-id">
            {{ t("profile.communityId") }}：{{ userStore.uid }}
          </div>
          <div class="profile-social">
            <div
              class="cursor-pointer profile-social-item followers-container"
              @click="goToFans"
            >
              <span class="profile-number">{{ userStore.fansCount || 0 }}</span>
              <span> {{ t("profile.followers") }}</span>
              <!-- 新增粉丝小红点 -->
              <div
                v-if="userStore.newFollowerCount > 0"
                class="new-follower-dot"
              ></div>
            </div>
            <div class="cursor-pointer profile-social-item" @click="goToFollow">
              <span class="profile-number"
                >{{ userStore.followCount || 0 }}
              </span>
              <span> {{ t("profile.follow") }}</span>
            </div>
            <div class="profile-social-item">
              <span class="profile-number">{{
                userStore.likesCount || 0
              }}</span>
              <span> {{ t("profile.likes") }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="languageStore.showPoint"
          class="profile-coin"
          @click="goToShop"
        >
          <img src="@/assets/coin.png" alt="coin" />
          <span>{{ t("profile.shopLabel") }}</span>
        </div>
      </div>
    </div>

    <!-- 导航栏 -->
    <div class="profile-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.key"
        class="tab"
        :class="{ active: currentTab === tab.key }"
        @click="handleTabChange(tab.key)"
      >
        {{ t(tab.title) }}
      </div>
    </div>

    <!-- 内容区域 -->
    <div
      class="profile-content transform-transition"
      :style="{ transform: `translateY(${pullDistance}px)` }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- 下拉刷新指示器 -->
      <div
        v-if="pullDistance > 0"
        class="pull-refresh-indicator"
        :class="{
          'is-refreshing': isRefreshing,
        }"
      >
        <Loading v-if="isRefreshing" :text="t('common.loading')" type="text" />
        <div v-else class="pull-text">
          {{
            pullDistance > pullThreshold
              ? t("home.releaseToRefresh")
              : t("home.pullToRefresh")
          }}
        </div>
      </div>

      <!-- 内容区域 -->
      <transition name="fade" mode="out-in">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-wrapper" key="loading">
          <Loading :text="t('common.loading')" />
        </div>
        <!-- 帖子列表 -->
        <div v-else class="content-wrapper" key="content">
          <transition-group name="list" tag="div" class="posts-list">
            <ThreadItem
              v-for="(post, index) in posts"
              :key="post.id"
              :post="post"
              :show-follow="true"
              @update:post="(updatedPost) => updateThread(index, updatedPost)"
            />
          </transition-group>

          <!-- 空状态 -->
          <Empty
            v-if="posts.length === 0"
            :tip="
              currentTab === 'posts'
                ? t('profile.noPosts')
                : t('profile.noFavorites')
            "
          />

          <!-- 加载更多提示 -->
          <div v-if="posts.length > 0">
            <div v-if="isLoadingMore" class="load-more-loading">
              <Loading type="text" :text="t('common.loadingMore')" />
            </div>
            <div v-else-if="hasMoreData" class="load-more-tip">
              <span>{{ t("common.loadMore") }}</span>
            </div>
            <div v-else class="load-more-end">
              <span>{{ t("common.noMore") }}</span>
            </div>
          </div>
        </div>
      </transition>
      <!-- 加载更多触发器 -->
      <div ref="loadMoreRef" class="load-more-trigger"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onActivated, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { clickButton, pageType } from "@/utils/point";
// @ts-ignore
import ThreadItem from "@/components/common/ThreadItem.vue";
import { useRouter } from "vue-router";
// @ts-ignore
import NavBar from "@/components/common/NavBar.vue";
// @ts-ignore
import Empty from "@/components/common/Empty.vue";
// @ts-ignore
import Loading from "@/components/common/Loading.vue";
// @ts-ignore
import Avatar from "@/components/common/Avatar.vue";
import { useUserStore, useToastStore } from "@/stores";
import { useThreadStore } from "@/stores/thread";
import { useFavoriteStore } from "@/stores/favorite";
import { getGameConfig } from "@/config/gameConfig";
import { useLanguageStore } from "@/stores/language";
const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const threadStore = useThreadStore();
const favoriteStore = useFavoriteStore();
const toast = useToastStore();
const gameConfig = getGameConfig();
// 下拉刷新相关状态
const isRefreshing = ref(false);
const pullDistance = ref(0);
const startY = ref(0);
const currentY = ref(0);
const pullThreshold = 80; // 下拉触发阈值
const maxPullDistance = 120; // 最大下拉距离
const languageStore = useLanguageStore();
// 懒加载和下拉加载更多
const loadMoreRef = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);
const profileBanner = computed(() => {
  return languageStore.getImage("profileBanner") || gameConfig.profileBanner;
});
function loadMore() {
  if (currentTab.value === "posts") {
    if (
      !threadStore.hasMoreUserThreads ||
      threadStore.loading ||
      threadStore.loadingMore
    ) {
      return;
    }
    threadStore.loadMoreUserThreads(userStore.uid as number);
  } else if (currentTab.value === "favorites") {
    if (
      !favoriteStore.hasMore ||
      favoriteStore.loading ||
      favoriteStore.loadingMore
    ) {
      return;
    }
    favoriteStore.loadMoreFavorites(userStore.uid as number);
  }
}

// 初始化IntersectionObserver
function initIntersectionObserver() {
  if (observer.value) {
    observer.value.disconnect();
  }

  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    },
    {
      rootMargin: "100px", // 提前100px触发加载
      threshold: 0.1,
    }
  );

  if (loadMoreRef.value) {
    observer.value.observe(loadMoreRef.value);
  }
}

const goToNicknameModify = () => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击修改昵称",
  });
  router.push("/nickname/modify");
};
const goToSetting = () => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击设置",
  });
  router.push("/setting");
};
const goToShop = () => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击兑换中心",
  });
  router.push("/shop");
};
const goToFans = () => {
  userStore.clearNewFollower();
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击粉丝列表",
  });
  router.push("/fans");
};
const goToFollow = () => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击关注列表",
  });
  router.push("/follow");
};
const goToAvatar = () => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "点击头像",
  });
  router.push("/avatar");
};

// 模拟标签数据
const tabs = [
  { key: "posts", title: "profile.tabs.posts" },
  { key: "favorites", title: "profile.tabs.favorites" },
];

const currentTab = ref("posts");

// 数据适配器：将不同格式的数据转换为 ThreadItem 需要的格式
const formatThreadData = (data: any[]) => {
  return data.map((item) => ({
    ...item,
    id: item.tid,
    time: item.dateline || item.create_time,
    content: item.message || item.content || "",
    message: item.message || "",
    views: item.views || 0,
    comments: item.replies || 0,
    likes: item.likes || 0,
    showFollow: false,
    tid: item.tid,
    author: item.author || "",
    authorid: item.authorid || 0,
    dateline: item.dateline || 0,
    subject: item.subject || "",
    image: item.images || [],
    is_author_followed: item.is_author_followed || 0,
    is_liked: item.is_liked || false,
    avatar: item.avatar || 1,
    image_links: item.image_links || [],
    video_links: item.video_links || [],
  }));
};

// 加载状态
const isLoading = computed(() => {
  if (currentTab.value === "posts") {
    return threadStore.loading;
  } else if (currentTab.value === "favorites") {
    return favoriteStore.loading;
  }
  return false;
});

// 帖子数据
const posts = computed(() => {
  if (currentTab.value === "posts") {
    return formatThreadData(threadStore.userThreads);
  } else if (currentTab.value === "favorites") {
    return formatThreadData(favoriteStore.favoriteList);
  }
  return [];
});

// 是否正在加载更多
const isLoadingMore = computed(() => {
  if (currentTab.value === "posts") {
    return threadStore.loadingMore;
  } else if (currentTab.value === "favorites") {
    return favoriteStore.loadingMore;
  }
  return false;
});

// 是否有更多数据
const hasMoreData = computed(() => {
  if (currentTab.value === "posts") {
    return threadStore.hasMoreUserThreads;
  } else if (currentTab.value === "favorites") {
    return favoriteStore.hasMore;
  }
  return false;
});

// 统一的数据获取函数
const fetchData = async (tabKey: string) => {
  if (!userStore.uid) return;

  try {
    if (tabKey === "posts") {
      await threadStore.getUserThreads(userStore.uid as number);
    } else if (tabKey === "favorites") {
      await favoriteStore.getFavoriteList(userStore.uid as number);
    }
  } catch (error) {
    console.error(
      `获取${tabKey === "posts" ? "用户帖子" : "收藏列表"}失败:`,
      error
    );
  }
};

// 下拉刷新相关方法
const handleTouchStart = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  startY.value = e.touches[0].clientY;
  currentY.value = startY.value;
};

const handleTouchMove = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  currentY.value = e.touches[0].clientY;
  const deltaY = currentY.value - startY.value;

  // 只处理向下滑动
  if (deltaY > 0) {
    e.preventDefault();
    const distance = Math.min(deltaY * 0.5, maxPullDistance);
    pullDistance.value = distance;
  }
};

const handleTouchEnd = async () => {
  if (pullDistance.value > pullThreshold && !isRefreshing.value) {
    // 触发刷新
    await handleRefresh();
  } else {
    // 重置状态
    resetPullState();
  }
};

const handleRefresh = async () => {
  isRefreshing.value = true;
  pullDistance.value = pullThreshold;

  try {
    // 重新获取当前tab的数据
    await fetchData(currentTab.value);
    toast.showToast(t("common.refreshSuccess"), "success", 1000);
  } catch (error) {
    console.error("刷新失败:", error);
    toast.showToast(t("common.refreshFailed"), "error", 1000);
  } finally {
    // 延迟重置状态，让用户看到成功提示
    setTimeout(() => {
      resetPullState();
    }, 1000);
  }
};

const resetPullState = () => {
  pullDistance.value = 0;
  isRefreshing.value = false;
};

// 更新帖子
const updateThread = (index: number, updatedPost: any) => {
  if (currentTab.value === "posts") {
    threadStore.userThreads[index] = updatedPost;
  } else if (currentTab.value === "favorites") {
    favoriteStore.favoriteList[index] = updatedPost;
    favoriteStore.updateFavoriteFollowStatus(
      updatedPost.authorid,
      updatedPost.is_author_followed
    );
  }
};

// 切换标签时加载对应数据
const handleTabChange = async (tabKey: string) => {
  clickButton({
    eventType: pageType.USER,
    eventReason: "切换帖子分类",
    eventValue: JSON.stringify({
      tab: tabKey,
    }),
  });
  currentTab.value = tabKey;
  await fetchData(tabKey);
};

// 组件激活时重新获取当前标签的数据（用于 keep-alive 场景）
onActivated(async () => {
  if (!userStore.checkLogin()) {
    return;
  }
  fetchData(currentTab.value);
});
onMounted(() => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  // 初始化IntersectionObserver
  initIntersectionObserver();
});

// 组件卸载时清理observer
onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<style lang="scss" scoped>
.profile-page {
  background-color: #f4f6fa;
  min-height: 100vh;
  padding-top: 56px;
  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  position: relative;
}

/* 下拉刷新指示器样式 */
.pull-refresh-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  background: transparent;
  transition: all 0.3s ease;
}

.pull-text {
  color: #999;
  font-size: 14px;
  text-align: center;
}

.profile-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 56px;
  border-bottom: 1px solid #f0f0f0;
  z-index: 1;
  background: #fff;
}

.profile-header-box {
  position: relative;
  width: 100%;
  height: 150px;
  padding: 80px 20px 0;
  background-size: cover;
}
.profile-title {
  font-size: 20px;
  font-weight: bold;
}
.profile-setting {
  position: absolute;
  right: 16px;
  width: 24px;
  height: 24px;
  top: 16px;
  cursor: pointer;
}
.profile-card {
  background: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 16px 8px;
  position: relative;
  padding-top: 40px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.profile-avatar {
  position: absolute;
  top: -32px;
  left: 20px;
  cursor: pointer;
}
.profile-info {
  flex: 1;
  margin-left: 16px;
}
.profile-name {
  font-size: 16px;
  font-weight: 600;
}
.profile-edit {
  color: #666;
  text-decoration: underline;
  font-size: 12px;
  margin-left: 8px;
  cursor: pointer;
}
.profile-titles {
  position: absolute;
  top: 0;
  left: 100px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 4px;
  align-items: center;
}
.profile-title-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #ffb400 0%, #ff9500 100%);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(255, 180, 0, 0.25);
  white-space: nowrap;
  line-height: 1.2;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.profile-id {
  color: #999;
  font-size: 12px;
  margin: 4px 0 10px;
}
.profile-social {
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: #666;
}
.profile-social-item {
  display: flex;
  align-items: center;
  gap: 4px;
  .profile-number {
    color: #333;
    font-size: 16px;
    font-weight: bold;
  }
}

// 新增粉丝小红点样式
.followers-container {
  position: relative;
}

.new-follower-dot {
  position: absolute;
  top: -2px;
  right: -6px;
  width: 5px;
  height: 5px;
  background-color: #df4c6e;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.profile-coin {
  position: absolute;
  right: 8px;
  top: 8px;
  display: flex;
  align-items: center;
  color: #ffb400;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #ffb400;
  border-radius: 8px;
  padding: 4px 8px;
  transition: all 0.3s ease;
}
.profile-coin:hover {
  background-color: rgba(255, 180, 0, 0.1);
}
.profile-coin:active {
  background-color: rgba(255, 180, 0, 0.2);
}
.profile-coin img {
  width: 16px;
  height: 16px;
  margin-right: 4px;
}
.profile-content {
  padding: 15px 10px 150px;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 0 0 12px 12px;
  min-height: 60vh;
  position: relative;
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.content-wrapper {
  position: relative;
  min-height: 300px;
}

.posts-list {
  position: relative;
}

.profile-tabs {
  display: flex;
  background-color: #fff;
  max-width: 800px;
  margin: 85px auto 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px 12px 0 0;
}
.tab {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  position: relative;
}
.tab.active {
  color: #333;
  font-weight: bold;
}
.tab.active::after {
  content: "";
  display: block;
  width: 60%;
  height: 3px;
  background: #ffb400;
  border-radius: 2px;
  margin: 0 auto;
  position: absolute;
  left: 20%;
  bottom: 0;
}

.profile-post {
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}
.post-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.post-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
}
.post-username {
  font-size: 15px;
  font-weight: 500;
}
.post-date {
  font-size: 12px;
  color: #aaa;
}
.post-content {
  margin: 10px 0;
  font-size: 16px;
}
.post-actions {
  display: flex;
  gap: 32px;
  margin-top: 8px;
}
.action {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #888;
  font-size: 14px;
}
.action img {
  width: 18px;
  height: 18px;
}
.profile-loadmore {
  text-align: center;
  color: #bbb;
  font-size: 14px;
  margin-top: 24px;
}

/* 加载更多相关样式 */
.load-more-loading,
.load-more-tip,
.load-more-end {
  text-align: center;
  padding: 20px 0;
  font-size: 14px;
}

.load-more-tip {
  color: #666;
}

.load-more-end {
  color: #999;
}

.load-more-trigger {
  height: 1px;
  width: 100%;
}
</style>
